import { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import { MdArrowBack as ArrowBackIcon } from 'react-icons/md';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SendIcon from '@mui/icons-material/Send';
import { useParams, useNavigate } from '@tanstack/react-router';
import {
  useGetQuiz,
  useStartAttempt,
  useSubmitAttempt,
  useGetUserAttempts,
  type TQuizQuestion,
} from '@onesource/services';
import type { TSubmitAnswer } from '@onesource/schemas';
import { QuestionRenderer } from './quiz/question-renderer';
import { QuizTimer } from './quiz/quiz-timer';
import { QuizProgress } from './quiz/quiz-progress';

/**
 * Quiz Player Page
 * Full interactive quiz-taking experience
 */
export const QuizPlayerPage = () => {
  const navigate = useNavigate();
  const { id: quizId } = useParams({ from: '/_protected/community-hub/quizzes/$id' });

  // Fetch quiz data
  const { data: quiz, isLoading: isLoadingQuiz, error: quizError } = useGetQuiz(quizId);
  const { data: userAttempts } = useGetUserAttempts(quizId);

  // Mutations
  const startAttempt = useStartAttempt();
  const submitAttempt = useSubmitAttempt();

  // Local state
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, TSubmitAnswer>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showTimeUpDialog, setShowTimeUpDialog] = useState(false);
  const [attemptStarted, setAttemptStarted] = useState(false);

  // Get sorted questions
  const questions: TQuizQuestion[] = quiz?.questions ? [...quiz.questions].sort((a, b) => a.position - b.position) : [];

  const currentQuestion = questions[currentQuestionIndex];

  // Check if max attempts reached
  const attemptsUsed = userAttempts?.length || 0;
  const maxAttemptsReached = quiz?.maxAttempts && attemptsUsed >= quiz.maxAttempts;

  // Initialize answers when quiz loads
  useEffect(() => {
    if (quiz && questions.length > 0 && Object.keys(answers).length === 0) {
      const initialAnswers: Record<string, TSubmitAnswer> = {};
      questions.forEach(q => {
        initialAnswers[q.id] = {
          questionId: q.id,
          selectedOptionIds: [],
          orderingSequence: [],
          textAnswer: '',
        };
      });
      setAnswers(initialAnswers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only initialize when quiz/questions load
  }, [quiz, questions]);

  // Start attempt when component mounts
  useEffect(() => {
    if (quiz && !attemptId && !maxAttemptsReached && !attemptStarted) {
      setAttemptStarted(true);
      startAttempt.mutate(quizId, {
        onSuccess: attempt => {
          setAttemptId(attempt.id);
        },
        onError: error => {
          console.error('Failed to start attempt:', error);
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- startAttempt is stable from React Query
  }, [quiz, quizId, attemptId, maxAttemptsReached, attemptStarted]);

  // Handle answer change
  const handleAnswerChange = useCallback((answer: TSubmitAnswer) => {
    setAnswers(prev => ({
      ...prev,
      [answer.questionId]: answer,
    }));
  }, []);

  // Navigation
  const goToPrevious = () => {
    setCurrentQuestionIndex(prev => Math.max(0, prev - 1));
  };

  const goToNext = () => {
    setCurrentQuestionIndex(prev => Math.min(questions.length - 1, prev + 1));
  };

  const goToQuestion = (index: number) => {
    setCurrentQuestionIndex(index);
  };

  // Check if question is answered
  const isQuestionAnswered = (questionId: string): boolean => {
    const answer = answers[questionId];
    if (!answer) {
      return false;
    }

    const question = questions.find(q => q.id === questionId);
    if (!question) {
      return false;
    }

    switch (question.questionType) {
      case 'multiple_choice':
      case 'multiple_select':
      case 'true_false': {
        return (answer.selectedOptionIds?.length || 0) > 0;
      }
      case 'short_answer': {
        return (answer.textAnswer?.trim().length || 0) > 0;
      }
      case 'ordering': {
        return (answer.orderingSequence?.length || 0) > 0;
      }
      default: {
        return false;
      }
    }
  };

  // Count answered questions
  const answeredCount = questions.filter(q => isQuestionAnswered(q.id)).length;

  // Handle time up
  const handleTimeUp = useCallback(() => {
    setShowTimeUpDialog(true);
  }, []);

  // Submit quiz
  const handleSubmit = async () => {
    if (!attemptId) {
      return;
    }

    setIsSubmitting(true);
    setShowSubmitDialog(false);
    setShowTimeUpDialog(false);

    try {
      const answersArray = Object.values(answers);
      const result = await submitAttempt.mutateAsync({
        attemptId,
        data: { answers: answersArray },
      });

      // Navigate to results page
      navigate({ to: '/community-hub/attempts/$id', params: { id: result.id } });
    } catch (error) {
      console.error('Failed to submit quiz:', error);
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (isLoadingQuiz) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (quizError) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load quiz: {quizError.message}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate({ to: '/community-hub' })} sx={{ mt: 2 }}>
          Back to Community Hub
        </Button>
      </Box>
    );
  }

  // Max attempts reached
  if (maxAttemptsReached) {
    return (
      <Box p={3}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          You have reached the maximum number of attempts ({quiz?.maxAttempts}) for this quiz.
        </Alert>
        <Typography variant="h5" gutterBottom>
          {quiz?.title}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          You have completed {attemptsUsed} attempt(s).
        </Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate({ to: '/community-hub' })}>
          Back to Community Hub
        </Button>
      </Box>
    );
  }

  // Waiting for attempt to start
  if (!attemptId || startAttempt.isPending) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px" gap={2}>
        <CircularProgress />
        <Typography color="text.secondary">Starting quiz...</Typography>
      </Box>
    );
  }

  // No questions
  if (questions.length === 0) {
    return (
      <Box p={3}>
        <Alert severity="info">This quiz has no questions yet.</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate({ to: '/community-hub' })} sx={{ mt: 2 }}>
          Back to Community Hub
        </Button>
      </Box>
    );
  }

  return (
    <Box p={3} maxWidth="900px" mx="auto">
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            {quiz?.title}
          </Typography>
          {quiz?.description && (
            <Typography variant="body2" color="text.secondary">
              {quiz.description}
            </Typography>
          )}
        </Box>

        {/* Timer */}
        {quiz?.timeLimitSeconds && (
          <QuizTimer totalSeconds={quiz.timeLimitSeconds} onTimeUp={handleTimeUp} paused={isSubmitting} />
        )}
      </Box>

      {/* Progress */}
      <Box mb={3}>
        <QuizProgress
          currentIndex={currentQuestionIndex}
          totalQuestions={questions.length}
          answeredCount={answeredCount}
        />
      </Box>

      {/* Question Navigation Pills */}
      <Box mb={3} sx={{ overflowX: 'auto' }}>
        <Stepper nonLinear activeStep={currentQuestionIndex}>
          {questions.map((q, index) => (
            <Step key={q.id} completed={isQuestionAnswered(q.id)}>
              <StepButton onClick={() => goToQuestion(index)}>{index + 1}</StepButton>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Current Question */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          {/* Question Type Badge */}
          <Box mb={2}>
            <Chip
              label={currentQuestion.questionType.replace('_', ' ').toUpperCase()}
              size="small"
              color="primary"
              variant="outlined"
            />
            <Chip
              label={`${currentQuestion.points} point${currentQuestion.points !== 1 ? 's' : ''}`}
              size="small"
              sx={{ ml: 1 }}
            />
          </Box>

          {/* Question Renderer */}
          <QuestionRenderer
            question={currentQuestion}
            answer={answers[currentQuestion.id] || { questionId: currentQuestion.id }}
            onAnswerChange={handleAnswerChange}
            disabled={isSubmitting}
          />
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={goToPrevious}
          disabled={currentQuestionIndex === 0 || isSubmitting}
        >
          Previous
        </Button>

        <Box display="flex" gap={2}>
          {currentQuestionIndex < questions.length - 1 ? (
            <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={goToNext} disabled={isSubmitting}>
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              endIcon={<SendIcon />}
              onClick={() => setShowSubmitDialog(true)}
              disabled={isSubmitting}
            >
              Submit Quiz
            </Button>
          )}
        </Box>
      </Box>

      {/* Unanswered Warning */}
      {answeredCount < questions.length && (
        <Alert severity="info" sx={{ mt: 3 }}>
          You have answered {answeredCount} of {questions.length} questions.
          {answeredCount < questions.length && ' Make sure to answer all questions before submitting.'}
        </Alert>
      )}

      {/* Submit Confirmation Dialog */}
      <Dialog open={showSubmitDialog} onClose={() => setShowSubmitDialog(false)}>
        <DialogTitle>Submit Quiz?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have answered {answeredCount} of {questions.length} questions.{' '}
            {answeredCount < questions.length && (
              <Box component="span" sx={{ color: 'warning.main', display: 'block', mt: 1 }}>
                Warning: You have {questions.length - answeredCount} unanswered question(s).
              </Box>
            )}
            Are you sure you want to submit your quiz?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSubmitDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Time Up Dialog */}
      <Dialog open={showTimeUpDialog} disableEscapeKeyDown>
        <DialogTitle>Time&apos;s Up!</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your time has expired. Your quiz will be submitted automatically with your current answers.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSubmit} variant="contained" color="primary" autoFocus>
            View Results
          </Button>
        </DialogActions>
      </Dialog>

      {/* Submitting Overlay */}
      {isSubmitting && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Submitting your quiz...
          </Typography>
        </Box>
      )}
    </Box>
  );
};
